import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { 
  Users, 
  Search, 
  Download, 
  Mail,
  Calendar,
  MapPin,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  X
} from 'lucide-react';

const GET_ALL_USERS = gql`
  query GetAllUsers($role: UserRole, $limit: Int, $offset: Int) {
    users(role: $role, limit: $limit, offset: $offset) {
      id
      name
      email
      phone
      role
      userTypes
      joinedDate
      tokenBalance
      location {
        city
        state
      }
      groups {
        id
        name
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      role
    }
  }
`;

const AdminUsers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('ALL');
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [page, setPage] = useState(0);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const limit = 50;

  const { loading, error, data, refetch } = useQuery(GET_ALL_USERS, {
    variables: {
      role: filterRole !== 'ALL' ? filterRole : undefined,
      limit: limit,
      offset: page * limit
    }
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: () => refetch()
  });

  const users = data?.users || [];

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.phone?.includes(searchTerm);
    return matchesSearch;
  });

  // Calculate stats
  const stats = {
    total: filteredUsers.length,
    buyers: users.filter(u => u.role === 'BUYER').length,
    vendors: users.filter(u => u.userTypes && u.userTypes.includes('vendor')).length,
    contributors: users.filter(u => u.role === 'CONTRIBUTOR').length,
    newThisWeek: users.filter(u => {
      const joinDate = new Date(u.joinedDate);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return joinDate > weekAgo;
    }).length
  };

  const toggleUserExpansion = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Role', 'Joined Date', 'Token Balance', 'Location', 'Groups'].join(','),
      ...filteredUsers.map(user => [
        user.name,
        user.email,
        user.phone || '',
        user.userTypes ? user.userTypes.join(', ') : user.role || 'User',
        user.joinedDate,
        user.tokenBalance,
        user.location ? `${user.location.city}, ${user.location.state}` : '',
        user.groups?.length || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRoleBadgeColor = (role) => {
    // Handle userTypes array
    if (Array.isArray(role)) {
      if (role.includes('admin')) return 'bg-red-100 text-red-800';
      if (role.includes('vendor')) return 'bg-purple-100 text-purple-800';
      role = role[0]; // fallback to first role
    }
    switch(role) {
      case 'BUYER': return 'bg-blue-100 text-blue-800';
      case 'VENDOR': return 'bg-purple-100 text-purple-800';
      case 'CONTRIBUTOR': return 'bg-green-100 text-green-800';
      case 'ADMIN': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Profile Modal Component
  const ProfileModal = ({ user, onClose }) => {
    if (!user) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">User Profile</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{user.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{user.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <div className="flex gap-1">
                  {user.userTypes && user.userTypes.length > 0 ? (
                    user.userTypes.map(type => (
                      <span key={type} className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(type.toUpperCase())}`}>
                        {type.toUpperCase()}
                      </span>
                    ))
                  ) : (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role || 'USER'}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">User ID</p>
                <p className="font-medium">{user.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Joined Date</p>
                <p className="font-medium">{formatDate(user.joinedDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Token Balance</p>
                <p className="font-medium">{user.tokenBalance || 0}</p>
              </div>
              {user.location && (
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-medium">{user.location.city}, {user.location.state}</p>
                </div>
              )}
            </div>
            
            {user.groups && user.groups.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Groups</p>
                <div className="space-y-1">
                  {user.groups.map(group => (
                    <div key={group.id} className="bg-gray-50 px-3 py-1 rounded">
                      {group.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Transactions Modal Component
  const TransactionsModal = ({ user, onClose }) => {
    // Demo transactions data - replace with real data later
    const demoTransactions = [
      { id: 1, date: '2024-01-15', type: 'Purchase', amount: 50, description: 'Group purchase', status: 'Completed' },
      { id: 2, date: '2024-01-20', type: 'Token Transfer', amount: 25, description: 'Reward tokens', status: 'Completed' },
      { id: 3, date: '2024-01-25', type: 'Refund', amount: -15, description: 'Order cancellation', status: 'Processed' },
    ];
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Transaction History - {user.name}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {demoTransactions.map(tx => (
                  <tr key={tx.id}>
                    <td className="px-4 py-2 text-sm">{tx.date}</td>
                    <td className="px-4 py-2 text-sm">{tx.type}</td>
                    <td className="px-4 py-2 text-sm">{tx.description}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={tx.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                        ${Math.abs(tx.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {demoTransactions.length} transactions
            </p>
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600">Error loading users</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Manage and monitor platform users</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
            >
              <Download size={20} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Users</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.buyers}</div>
            <div className="text-sm text-gray-600">Buyers</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.vendors}</div>
            <div className="text-sm text-gray-600">Vendors</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.contributors}</div>
            <div className="text-sm text-gray-600">Contributors</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{stats.newThisWeek}</div>
            <div className="text-sm text-gray-600">New This Week</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Roles</option>
            <option value="BUYER">Buyers</option>
            <option value="VENDOR">Vendors</option>
            <option value="CONTRIBUTOR">Contributors</option>
            <option value="ADMIN">Admins</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => {
              const isExpanded = expandedUsers.has(user.id);
              
              return (
                <React.Fragment key={user.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900 flex items-center">
                          <Mail size={14} className="mr-1" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="text-sm text-gray-500">{user.phone}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-1">
                        {user.userTypes && user.userTypes.length > 0 ? (
                          user.userTypes.map(type => (
                            <span key={type} className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(type.toUpperCase())}`}>
                              {type.toUpperCase()}
                            </span>
                          ))
                        ) : (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                            {user.role || 'USER'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.joinedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div>{user.groups?.length || 0} groups</div>
                        <div>{user.tokenBalance || 0} tokens</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => toggleUserExpansion(user.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                    </td>
                  </tr>
                  
                  {isExpanded && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium mb-2">User Details</h4>
                            <div className="space-y-2 text-sm">
                              {user.location && (
                                <div className="flex items-center">
                                  <MapPin size={14} className="mr-2" />
                                  {user.location.city}, {user.location.state}
                                </div>
                              )}
                              <div className="flex items-center">
                                <Calendar size={14} className="mr-2" />
                                Member since {formatDate(user.joinedDate)}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Groups</h4>
                            {user.groups && user.groups.length > 0 ? (
                              <div className="space-y-1">
                                {user.groups.map(group => (
                                  <div key={group.id} className="text-sm">
                                    • {group.name}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No groups joined</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-4 flex gap-2">
                          <button 
                            onClick={() => {
                              setSelectedUserProfile(user);
                              setShowProfileModal(true);
                            }}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            View Profile
                          </button>
                          <button 
                            onClick={() => {
                              const subject = encodeURIComponent('Message from Admin');
                              const body = encodeURIComponent(`Hello ${user.name},\n\n`);
                              window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
                            }}
                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            Send Message
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedUserProfile(user);
                              setShowTransactionsModal(true);
                            }}
                            className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                          >
                            View Transactions
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <Users size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProfileModal && (
        <ProfileModal 
          user={selectedUserProfile} 
          onClose={() => setShowProfileModal(false)} 
        />
      )}
      
      {showTransactionsModal && (
        <TransactionsModal 
          user={selectedUserProfile} 
          onClose={() => setShowTransactionsModal(false)} 
        />
      )}
    </div>
  );
};

export default AdminUsers;